import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Box, ListItemIcon, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getChildClinicsWithParentTemplate } from 'apollo/templates/user/query.graphql';
import { LinkedClinic } from 'models/order';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { Organization } from 'models/organization';

type LinkedClinicsProps = {
    orderLinkedClinics: LinkedClinic[];
    onAddClinics: (clinics: LinkedClinic[]) => void;
};
const LinkedClinics: FC<LinkedClinicsProps> = ({ orderLinkedClinics, onAddClinics }: LinkedClinicsProps) => {
    const { t: translate } = useTranslation('orders');
    const [clinics, setClinics] = useState<Organization[]>();
    const [notLinkedClinics, setNotLinkedClinics] = useState<Organization[]>();
    const [linkedClinics, setLinkedClinics] = useState<LinkedClinic[]>([]);
    const [inputValue, setInputValue] = useState('');
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        setLinkedClinics(orderLinkedClinics || []);
    }, [orderLinkedClinics]);

    useQuery(getChildClinicsWithParentTemplate, {
        variables: {
            parentClinicId: (currentUser.organization as Organization)?._id,
        },
        onCompleted: ({ childClinicsWithParent }) => {
            setClinics(childClinicsWithParent);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (clinics && linkedClinics) {
            const notLinked = clinics.filter((clinic) => {
                return !linkedClinics.find((linkedClinic) => {
                    return linkedClinic.clinic._id === clinic._id;
                });
            });
            setNotLinkedClinics(notLinked);
        }
    }, [linkedClinics, clinics]);

    const addLinkedClinic = (clinic: Organization) => {
        if (linkedClinics) {
            const clinics = [
                ...linkedClinics,
                {
                    clinic: {
                        _id: clinic._id,
                        contact: {
                            email: clinic.contact.communication?.email,
                        },
                        name: clinic.name,
                    },
                },
            ] as LinkedClinic[];
            setLinkedClinics(clinics);
            onAddClinics(clinics);
        }
    };

    return (
        <Box>
            <Box>
                <Autocomplete
                    fullWidth
                    disableClearable
                    options={notLinkedClinics || []}
                    getOptionLabel={(clinic) => clinic.name}
                    getOptionDisabled={(clinic) => !clinic.contact?.communication?.email || false}
                    renderOption={(props, clinic) => (
                        <MenuItem {...props} key={clinic._id} value={clinic._id}>
                            <ListItemIcon>
                                <AddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>
                                {clinic.name}
                                <Typography variant="body2" color="grey.700">
                                    {clinic.contact.communication?.email}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label={translate('orders:ADD_PARTICIPANT_CLINIC')} />
                    )}
                    inputValue={inputValue}
                    onChange={(_, clinic) => {
                        addLinkedClinic(clinic);
                    }}
                    onInputChange={(_, value, reason) => {
                        if (reason === 'reset') {
                            setInputValue('');
                            return;
                        }
                        setInputValue(value);
                    }}
                />
            </Box>
        </Box>
    );
};

export default LinkedClinics;
